/**
 * ForgotPasswordForm -- Standalone forgot password page.
 *
 * Full-page centered layout for when a user navigates directly to /forgot-password.
 * Uses the shared Input and Button components with Zod validation.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { Mail, ArrowLeft } from 'lucide-react'
import { useForgotPassword } from '@/lib/auth'
import { Button } from '@/shared/components/forms/Button'
import { Input } from '@/shared/components/forms/Input'

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email address'),
})

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const forgotMutation = useForgotPassword()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    const result = forgotSchema.safeParse({ email: email.trim() })
    if (!result.success) {
      setValidationError(result.error.issues[0]?.message ?? 'Invalid email')
      return
    }

    const response = await forgotMutation.mutateAsync({
      email: result.data.email,
    })

    if (response.success) {
      setSuccess(true)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-2.5rem)] items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl bg-ue-bg-dark/80 border border-ue-border-medium/60 p-6">
        {success ? (
          /* Success state */
          <>
            <div className="p-3 bg-ue-accent-blue/10 border border-ue-accent-blue/20 rounded text-ue-accent-blue text-xs mb-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium">Check your email</span>
              </div>
              <p className="text-ue-accent-blue/80">
                If an account exists with that email, we've sent a password
                reset link. The link expires in 1 hour.
              </p>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-xs text-ue-text-tertiary hover:text-ue-text-secondary transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to dashboard
            </Link>
          </>
        ) : (
          /* Form state */
          <>
            <h2 className="text-sm font-medium text-ue-text-primary mb-1">
              Reset your password
            </h2>
            <p className="text-xs text-ue-text-secondary mb-4">
              Enter your email and we'll send you a link to reset your password.
            </p>

            {/* Server error */}
            {forgotMutation.data && !forgotMutation.data.success && (
              <div className="p-2 mb-3 bg-ue-accent-red/10 border border-ue-accent-red/20 rounded text-ue-accent-red text-xs">
                {forgotMutation.data.error || 'Request failed'}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                label="Email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={validationError ?? undefined}
                disabled={forgotMutation.isPending}
              />
              <Button
                type="submit"
                loading={forgotMutation.isPending}
                className="w-full"
              >
                Send Reset Link
              </Button>
            </form>

            <Link
              to="/"
              className="mt-4 flex items-center gap-1.5 text-xs text-ue-text-tertiary hover:text-ue-text-secondary transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
